import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة العمولة - احسب عمولة المبيعات والنسبة المئوية بدقة";
const DESCRIPTION = "حاسبة العمولة المجانية - احسب قيمة عمولة المبيعات، إجمالي المبلغ بعد العودة، ونسبة الربح بسهولة. أداة دقيقة للبائعين والشركات لحساب المستحقات المالية فوراً.";
const KEYWORDS = "حاسبة العمولة, حساب عمولة المبيعات, برنامج حساب العمولات, كيف احسب عمولتي, commission calculator, حساب النسبة المئوية للمبيعات";
const OG_URL = "https://do-calculate.com/calculator/ar/commission-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة العمولة",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "FinancialApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة العمولة",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كيف يتم حساب قيمة العمولة؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يتم حساب العمولة عن طريق ضرب إجمالي قيمة المبيعات في نسبة العمولة المئوية ثم تقسيم الناتج على 100. على سبيل المثال، إذا كانت المبيعات 1000 ريال والنسبة 5%، فإن العمولة تساوي 50 ريالاً.",
        },
      },
      {
        "@type": "Question",
        name: "ما الفرق بين العمولة البسيطة والعمولة المتدرجة؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "العمولة البسيطة تُحتسب كنسبة ثابتة من إجمالي المبيعات مهما بلغت قيمتها، أما العمولة المتدرجة فتتغير نسبتها بناءً على حجم المبيعات المحقق، حيث تزداد النسبة كلما زادت المبيعات لتحفيز البائع.",
        },
      },
      {
        "@type": "Question",
        name: "هل تشمل العمولة ضريبة القيمة المضافة؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "في العادة، تُحسب العمولة على صافي سعر البيع قبل إضافة ضريبة القيمة المضافة، إلا إذا نص العقد بين الطرفين على غير ذلك.",
        },
      },
    ],
  },
];

function CommissionCalculator() {
  const [baseAmount, setBaseAmount] = useState("");
  const [commissionRate, setCommissionRate] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateCommission = () => {
    try {
      setError("");
      const amount = parseFloat(baseAmount);
      const rate = parseFloat(commissionRate);
      const qty = parseInt(quantity) || 1;

      if (isNaN(amount) || isNaN(rate)) {
        setResult(null);
        return;
      }

      if (amount < 0 || rate < 0) {
        setError("يرجى إدخال قيم موجبة");
        setResult(null);
        return;
      }

      const totalSales = amount * qty;
      const commissionAmount = (totalSales * rate) / 100;
      const remainingAmount = totalSales - commissionAmount;

      setResult({
        totalSales,
        commissionAmount,
        remainingAmount,
        rate,
        qty,
      });
    } catch (err) {
      setError("حدث خطأ في الحساب");
      setResult(null);
    }
  };

  useEffect(() => {
    calculateCommission();
  }, [baseAmount, commissionRate, quantity]);

  const handleClear = () => {
    setBaseAmount("");
    setCommissionRate("");
    setQuantity(1);
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة العمولة</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تعد <strong>حاسبة العمولة</strong> أداة مالية لا غنى عنها لكل مسوق، بائع، أو صاحب عمل يرغب في <strong>حساب الأرباح</strong> والنسب المئوية بدقة. سواء كنت تعمل في العقارات، التجارة الإلكترونية، أو المبيعات المباشرة، تساعدك هذه الحاسبة على معرفة مستحقاتك فوراً بناءً على إجمالي المبيعات ونسبة العمولة المتفق عليها.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  أدخل بيانات المبيعات والنسبة
                </h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label >سعر الوحدة / المبلغ الأساسي</label>
                  <input
                    type="number"
                    className="form-control"
                    value={baseAmount}
                    onChange={(e) => setBaseAmount(e.target.value)}
                    placeholder="مثال: 1000"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >نسبة العمولة (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value)}
                    placeholder="مثال: 5"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label >الكمية (عدد المبيعات)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  امسح البيانات
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>نتائج الحساب:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>البند</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>القيمة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>إجمالي المبيعات</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>
                            {result.totalSales.toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>قيمة العمولة المستحقة</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.commissionAmount.toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>المبلغ الصافي بعد العمولة</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#0f3d28" }}>
                            {result.remainingAmount.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل المعادلة</strong>
                      </div>
                      <div className="info-section-body">
                        <p>العمولة = (إجمالي المبيعات × نسبة العمولة) ÷ 100</p>
                        <p>الحساب: ({result.totalSales} × {result.rate}%) = {result.commissionAmount}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                ### ما هي حاسبة العمولة وكيف تستخدمها؟
              </div>
              <div className="info-section-body">
                <p>حاسبة العمولة هي أداة رقمية مصممة لمساعدة الأفراد والشركات على تحديد المبالغ المالية التي يستحقها الوكيل أو البائع مقابل إتمام صفقة معينة. تعتمد العملية على نسبة مئوية من سعر البيع الإجمالي.</p>
                <p><strong>خطوات الاستخدام:</strong></p>
                <ul>
                  <li>أدخل سعر المنتج أو الخدمة التي تم بيعها.</li>
                  <li>حدد نسبة العمولة المتفق عليها (مثل 5% أو 10%).</li>
                  <li>أدخل عدد الوحدات المباعة في خانة الكمية.</li>
                  <li>ستقوم الحاسبة آلياً بإظهار إجمالي مبلغ العمولة والمبلغ المتبقي للشركة.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### استخدامات حاسبة العمولة في الواقع
              </div>
              <div className="info-section-body">
                <ul>
                  <li><strong>الوساطة العقارية:</strong> لحساب نصيب السمسار من قيمة بيع العقارات أو الإيجارات.</li>
                  <li><strong>مندوبي المبيعات:</strong> لمتابعة الأرباح الشهرية بناءً على الأهداف المحققة (Target).</li>
                  <li><strong>التجارة الإلكترونية:</strong> حساب الرسوم التي تأخذها المنصات مثل أمازون أو نون من التاجر.</li>
                  <li><strong>التسويق بالعمولة:</strong> معرفة الأرباح المتوقعة من ترويج الروابط والمنتجات الرقمية.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### مفاهيم أساسية في نظام العمولات
              </div>
              <div className="info-section-body">
                <p>تختلف أنظمة العمولات باختلاف طبيعة العمل، فبعضها يعتمد على "العمولة الصافية" والبعض الآخر يستخدم نظام "الراتب زائد العمولة".</p>
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th>النوع</th>
                      <th>الوصف</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>عمولة ثابتة</td>
                      <td>مبلغ محدد لكل عملية بيع بغض النظر عن السعر.</td>
                    </tr>
                    <tr>
                      <td>نسبة مئوية</td>
                      <td>حصة من إجمالي قيمة الفاتورة.</td>
                    </tr>
                    <tr>
                      <td>عمولة متدرجة</td>
                      <td>تزيد النسبة كلما زاد حجم المبيعات الإجمالي.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### أمثلة عملية لحساب العمولة
              </div>
              <div className="info-section-body">
                <p><strong>المثال الأول:</strong> بائع أجهزة منزلية باع غسالة بسعر 2000 ريال، وكانت نسبته 3%. العمولة = 2000 * 0.03 = 60 ريالاً.</p>
                <p><strong>المثال الثاني:</strong> مسوق عقاري باع شقة بقيمة 500,000 ريال بنسبة عمولة 2.5%. العمولة = 500,000 * 0.025 = 12,500 ريال.</p>
                <p><strong>المثال الثالث:</strong> بيع 10 قطع من منتج سعر الواحد 100 ريال بنسبة 10%. إجمالي المبيعات 1000، والعمولة 100 ريال.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### نصائح لضمان حساب عمولة صحيح
              </div>
              <div className="info-section-body">
                <ul>
                  <li>تأكد دائماً ما إذا كانت النسبة تُحسب قبل أو بعد ضريبة القيمة المضافة.</li>
                  <li>احتفظ بسجل دقيق لكل الفواتير لمطابقتها مع نتائج الحاسبة.</li>
                  <li>في حال وجود مرتجعات، تذكر خصم قيمتها من إجمالي مبيعاتك قبل حساب العمولة النهائية.</li>
                  <li>انتبه للكسور العشرية في النسب المئوية الكبيرة لتجنب فروقات المبالغ.</li>
                </ul>
              </div>
            </div>
          </div>

          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>أسئلة متكررة</h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كيف أحسب عمولتي من المبيعات يدوياً؟</h3>
              </div>
              <div className="info-section-body">
                لحساب العمولة يدوياً، اضرب إجمالي قيمة المبيعات في النسبة المئوية واقسم على 100. فمثلاً، إذا بعت بضاعة بـ 5000 ريال ونسبتك 2%، اضرب 5000 في 2 لتحصل على 10000، ثم اقسم على 100 ليكون الناتج 100 ريال. هذه الطريقة التقليدية تضمن لك معرفة ربحك من كل صفقة بشكل سريع.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ما هي نسبة العمولة المتعارف عليها في العقارات؟</h3>
              </div>
              <div className="info-section-body">
                في معظم الدول العربية، تتراوح نسبة عمولة السعي أو الوساطة العقارية بين 2% إلى 2.5% من قيمة العقار. يتحمل هذه النسبة عادة المشتري أو المستأجر، ولكن قد تختلف الاتفاقات بحسب طبيعة العقد المبرم بين الأطراف. من المهم توثيق هذه النسبة في عقد الوساطة لضمان حقوق الجميع.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل تحسب العمولة على السعر الإجمالي أم الربح؟</h3>
              </div>
              <div className="info-section-body">
                في أغلب الأنظمة التجارية، تُحسب العمولة على إجمالي سعر البيع النهائي للفاتورة. ومع ذلك، هناك شركات تفضل منح عمولة بناءً على "هامش الربح" (الفرق بين سعر البيع والتكلفة)، وهذا النوع يحفز المندوبين على البيع بأسعار أعلى لزيادة ربح الشركة وربحهم الشخصي.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ماذا يعني نظام العمولة المتدرجة؟</h3>
              </div>
              <div className="info-section-body">
                نظام العمولة المتدرجة هو أسلوب تحفيزي تزيد فيه النسبة المئوية كلما حقق البائع مبيعات أكثر. مثال: قد تأخذ 5% على أول 10,000 ريال مبيعات، ثم تزيد النسبة إلى 7% على المبالغ التي تتجاوز ذلك. هذا النظام يهدف إلى تشجيع الموظفين على تجاوز أهداف المبيعات المحددة لهم.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كيف أتعامل مع ضريبة القيمة المضافة عند حساب العمولة؟</h3>
              </div>
              <div className="info-section-body">
                القاعدة العامة هي حساب العمولة على قيمة المنتج الأصلية قبل إضافة الضريبة. إذا كان سعر المنتج 100 ريال والضريبة 15 ريال، فإن عمولتك تُحسب على الـ 100 ريال فقط. حساب العمولة على شامل الضريبة قد يؤدي لخلل مالي في حسابات الشركة لأن الضريبة هي مبلغ يتم تحصيله لصالح الدولة.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل هناك حد أقصى للعمولة قانوناً؟</h3>
              </div>
              <div className="info-section-body">
                القوانين تختلف من بلد لآخر ومن قطاع لآخر. في القطاع العقاري مثلاً، تضع بعض الوزارات سقفاً أعلى للعمولات لمنع الاستغلال. أما في عقود التوظيف والمبيعات الخاصة، فالأمر يعود عادة للاتفاق الرضائي بين الطرفين طالما لا يخالف قوانين العمل المحلية.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ما الفرق بين العمولة والمكافأة (Bonus)؟</h3>
              </div>
              <div className="info-section-body">
                العمولة هي مبلغ مرتبط مباشرة بكل عملية بيع فردية وتعتبر جزءاً أساسياً من الدخل. أما المكافأة فهي مبلغ إضافي يُمنح عادة عند تحقيق هدف جماعي أو سنوي، ولا تُحسب كنسبة من كل فاتورة. العمولات هي وقود البائع اليومي بينما المكافآت هي تقدير للأداء العام المتميز.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كيف أحسب عمولة المرتجعات؟</h3>
              </div>
              <div className="info-section-body">
                عند استرجاع العميل للمنتج، يتم خصم قيمة العمولة التي صُرفت للبائع من مستحقاته القادمة. الشركات تقوم عادة بعمل "مقاصة" شهرية، حيث تُجمع العمولات المستحقة وتُطرح منها عمولات المرتجعات لضمان أن البائع يتقاضى عمولة فقط على الصفقات الناجحة والنهائية.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommissionCalculator;